import { getStatusPage } from '@/api/cloudStatus';
import type { CloudStatusNotice, StatusPagePayload } from '@/types';
import { isSafeUrl } from '@/utils/branding';

const POLL_INTERVAL = 120_000;
const DISMISSED_STORAGE_KEY = 'dismissedCloudStatusNotices';
const FALLBACK_RELOADS_KEY = 'wbFallbackReloads';
const DISMISSED_KEPT = 20;
const TEXT_LIMIT = 600;
const LINE_SEPARATOR = /^[ \t]*---[ \t]*$/m;
const INLINE_SEPARATOR = /\s+---\s+/;

const SEVERITY_BY_STYLE = new Map<string, CloudStatusNotice['severity']>([
  ['danger', 'error'],
  ['info', 'info'],
  ['light', 'info'],
  ['dark', 'info'],
]);

const clamp = (text: string) =>
  (text.length > TEXT_LIMIT ? `${text.slice(0, TEXT_LIMIT)}…` : text);

export const pickLocalizedText = (text: string, locale: string) => {
  const source = String(text || '');
  const separator = LINE_SEPARATOR.test(source) ? LINE_SEPARATOR : INLINE_SEPARATOR;
  const parts = source.split(separator);

  if (parts.length < 2) {
    return clamp(source.trim());
  }

  const [ru, en] = [parts[0].trim(), parts[1].trim()];

  return clamp(locale === 'ru' ? ru || en : en || ru);
};

export const selectNotices = (payload: StatusPagePayload): CloudStatusNotice[] => {
  const incidents = (payload.incidents ?? [])
    .filter(({ active }) => active !== false)
    .map((incident) => ({
      key: `incident:${incident.id}:${incident.lastUpdatedDate ?? ''}`,
      title: incident.title,
      text: incident.content,
      severity: SEVERITY_BY_STYLE.get(incident.style ?? '') ?? 'warn',
    }));

  const maintenances = (payload.maintenanceList ?? [])
    .filter(({ status }) => status === 'under-maintenance')
    .map((maintenance) => ({
      key: `maintenance:${maintenance.id}:${maintenance.status}`,
      title: maintenance.title,
      text: maintenance.description,
      severity: 'warn' as const,
    }));

  return [...incidents, ...maintenances];
};

const releaseFallbackReloadBudget = () => {
  try {
    sessionStorage.removeItem(FALLBACK_RELOADS_KEY);
  } catch {
  }
};

export const useCloudStatusStore = createGlobalState(() => {
  releaseFallbackReloadBudget();

  const envUrl = (import.meta.env.SERVICE_STATUS_API_URL as string) || '';
  const apiUrl = isSafeUrl(envUrl) ? envUrl : '';
  const notices = ref<CloudStatusNotice[]>([]);
  const dismissedKeys = useStorage<string[]>(DISMISSED_STORAGE_KEY, []);
  const visibility = useDocumentVisibility();

  let attemptedAt = 0;

  const notice = computed(
    () => notices.value.find(({ key }) => !dismissedKeys.value.includes(key)) ?? null,
  );

  const load = async () => {
    attemptedAt = Date.now();

    try {
      notices.value = selectNotices(await getStatusPage(apiUrl));
    } catch {
    }
  };

  const dismiss = () => {
    if (notice.value) {
      dismissedKeys.value = [notice.value.key, ...dismissedKeys.value].slice(0, DISMISSED_KEPT);
    }
  };

  if (apiUrl) {
    useIntervalFn(
      () => {
        if (visibility.value !== 'hidden') {
          load();
        }
      },
      POLL_INTERVAL,
      { immediateCallback: true },
    );

    watch(visibility, (current) => {
      if (current === 'visible' && Date.now() - attemptedAt > POLL_INTERVAL / 2) {
        load();
      }
    });
  }

  return { notice, dismiss };
});
